import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { useTransactions } from "@/hooks/use-api";
import { ListPage } from "@/components/v2/list-page";
import { DataTable } from "@/components/v2/data-table";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { formatCurrency, formatDate } from "@/lib/utils";

type Row = Record<string, unknown>;

// transaction_type is free-text imported data (no DB enum), so the type chips
// are a curated set of the values that actually appear in the ledger. A type
// not listed here is still visible under "All" — the chips are a shortcut, not
// an exhaustive filter. See ADV-1145 research for the schema rationale.
const COMMON_TRANSACTION_TYPES = [
  "payment",
  "refund",
  "commission",
  "expense",
] as const;

type ApprovalFilter = "all" | "approved" | "pending";

// Derive a display status (Pattern C entity has no status column — UX shows a
// dot+label badge per ADR-0019 perennial-entity rule).
function deriveStatus(row: Row): string {
  const reviewStatus = row.review_status as string | null;
  if (reviewStatus) return reviewStatus.toLowerCase();
  return row.is_approved ? "approved" : "pending";
}

const columns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "transaction_date",
    header: "Date",
    cell: ({ row }) => (
      <span className="text-[color:var(--color-text-md)]">
        {formatDate(row.original.transaction_date as string)}
      </span>
    ),
  },
  {
    accessorKey: "display_id",
    header: "Reference",
    enableSorting: false,
    cell: ({ row }) => (
      <span className="typo-mono font-medium">
        {(row.original.display_id as string) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "transaction_type",
    header: "Type",
    cell: ({ row }) => {
      const type = row.original.transaction_type as string | null;
      return (
        <span className={type ? "" : "text-[color:var(--color-text-lo)]"}>
          {type
            ? type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
            : "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "amount",
    header: "Amount",
    meta: { numeric: true },
    cell: ({ row }) => {
      const amount = row.original.amount as number | null;
      return (
        <span className="font-medium">
          {amount != null ? formatCurrency(amount) : "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "vendor_name",
    header: "Vendor",
    enableSorting: false,
    cell: ({ row }) => {
      const vendor = row.original.vendor_name as string | undefined;
      return (
        <span className={vendor ? "" : "text-[color:var(--color-text-lo)]"}>
          {vendor || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "invoice_id",
    header: "Invoice",
    enableSorting: false,
    cell: ({ row }) => (
      <span
        className={
          row.original.invoice_id
            ? "text-[color:var(--color-text-md)]"
            : "text-[color:var(--color-text-lo)]"
        }
      >
        {row.original.invoice_id ? "Linked" : "—"}
      </span>
    ),
  },
  {
    accessorKey: "opportunity_id",
    header: "Deal",
    enableSorting: false,
    cell: ({ row }) => (
      <span
        className={
          row.original.opportunity_id
            ? "text-[color:var(--color-text-md)]"
            : "text-[color:var(--color-text-lo)]"
        }
      >
        {row.original.opportunity_id ? "Linked" : "—"}
      </span>
    ),
  },
  {
    id: "status",
    header: "Status",
    enableSorting: false,
    cell: ({ row }) => <StatusBadge status={deriveStatus(row.original)} />,
  },
];

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      variant={active ? "secondary" : "ghost"}
      size="sm"
      onClick={onClick}
      aria-pressed={active}
    >
      {children}
    </Button>
  );
}

export default function V2TransactionsPage() {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([
    { id: "transaction_date", desc: true },
  ]);
  const [pageIndex, setPageIndex] = useState(0);
  const [approval, setApproval] = useState<ApprovalFilter>("all");
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);
  const pageSize = 50;

  const { data, isLoading } = useTransactions({
    offset: pageIndex * pageSize,
    limit: pageSize,
    sort_by: sorting[0]?.id,
    sort_order: sorting[0]?.desc ? "desc" : "asc",
    is_approved:
      approval === "all"
        ? undefined
        : approval === "approved"
          ? "true"
          : "false",
    transaction_type: typeFilter,
  });

  const transactions = (data?.transactions ?? []) as Row[];
  const total = data?.total ?? 0;

  function resetPage<T>(setter: (v: T) => void, value: T) {
    setter(value);
    setPageIndex(0);
  }

  const filtersActive = approval !== "all" || typeFilter !== undefined;

  return (
    <ListPage
      title="Transactions"
      subtitle={`${total.toLocaleString()} total transactions`}
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="typo-label uppercase tracking-[0.08em] text-[color:var(--color-text-lo)] mr-1">
          Status
        </span>
        <FilterChip
          active={approval === "all"}
          onClick={() => resetPage(setApproval, "all")}
        >
          All
        </FilterChip>
        <FilterChip
          active={approval === "approved"}
          onClick={() => resetPage(setApproval, "approved")}
        >
          Approved
        </FilterChip>
        <FilterChip
          active={approval === "pending"}
          onClick={() => resetPage(setApproval, "pending")}
        >
          Pending
        </FilterChip>

        <span className="typo-label uppercase tracking-[0.08em] text-[color:var(--color-text-lo)] ml-3 mr-1">
          Type
        </span>
        {COMMON_TRANSACTION_TYPES.map((type) => (
          <FilterChip
            key={type}
            active={typeFilter === type}
            onClick={() =>
              resetPage(setTypeFilter, typeFilter === type ? undefined : type)
            }
          >
            {type.replace(/\b\w/g, (c) => c.toUpperCase())}
          </FilterChip>
        ))}

        {filtersActive && (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto text-[color:var(--color-text-md)]"
            onClick={() => {
              setApproval("all");
              setTypeFilter(undefined);
              setPageIndex(0);
            }}
            aria-label="Clear filters"
          >
            Clear
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        data={transactions}
        total={total}
        isLoading={isLoading}
        onRowClick={(row) => navigate(`/transactions/${row.id}`)}
        emptyMessage="No transactions"
        emptyDescription={
          filtersActive
            ? "No transactions match the active filters. Clear them to see all transactions."
            : "Transactions appear here as financial events are recorded."
        }
        pageSize={pageSize}
        pageIndex={pageIndex}
        onPaginationChange={setPageIndex}
        sorting={sorting}
        onSortingChange={setSorting}
      />
    </ListPage>
  );
}
